import * as React from 'react'
import { LinkProps } from '@amzn/dvui/dist/Link'
import { StringId } from '../localizer/stringId'
import { TextLocalizer, withLocalization } from '../localizer/localizer'
import { getDevicesList } from '../../actions/devices'
import { Optional, RequestContext, SettingsWidgetArgs } from 'DVWebNodeUtils'
import { IDetailPageState } from '../../reducers'
import { connect } from 'react-redux'
import { simpleSelectors } from '../../selectors/basicState'
import { FeedbackFormModal, FeedbackFormModalContent, FormParams } from './feedbackFormModal'
import { isMobileSelector } from '../../selectors/pageContext'
import { getMarketplaceUrl } from '../../utils/marketplace'
import { isRetailSelector } from '../../selectors/requestContext'
import { buildDetailPageUrl } from '../../utils/url'
import { DeviceEntries, Devices } from '../../reducers/devices'
import { ILogger } from '../../context/withLogger'
import { stringifyOnClient } from '../../utils/utils'

const DROPLIST_ID = 'feedback-device-list'
const FORM_URL_BASE = 'https://digprjsurvey.amazon.com/api/feedbacks/AIV'
const LINK_TEXT = <TextLocalizer stringID={StringId.DV_WEB_FEEDBACK_feedback} />
const TITLE_TEXT = <TextLocalizer stringID={StringId.DV_WEB_FEEDBACK__send_us_feedback} />

type DropListEntry = {
  name: string,
  deviceId: string,
  text: string | JSX.Element,
}

const WEBSITE_ENTRY: DropListEntry = {
  deviceId: 'A13Q6A55DBZB7M',
  name: 'AVOD Website',
  text: <TextLocalizer stringID={StringId.DV_WEB_FEEDBACK_no_device_website} />
}

const FALLBACK_ENTRY: DropListEntry = {
  deviceId: '',
  name: 'Other Device',
  text: <TextLocalizer stringID={StringId.DV_WEB_SETTINGS_HEAD_YOUR_DEVICES} />,
}

interface FeedbackFormState{
  isOpen: boolean,
  selected: number,
}

export interface FeedbackFormProps {
  devices: Devices,
  isMobile: boolean,
  isRetail: boolean,
  context: RequestContext,
  pageTitleId: string,
}

// Transform to droplist entry and wipe out redundancy by device name
export const deviceEntryToDropListEntry = (deviceEntries: DeviceEntries, signedIn: boolean = true) => {
  const entries = Optional.from(deviceEntries)
  // For signed-in users, when the request fails (or is pending, for the sake of better user experience)
  // show an extra fallback option 'Your Devices'
  if (entries.isEmpty() && signedIn) {
    return [WEBSITE_ENTRY, FALLBACK_ENTRY]
  }
  // or the request succeeds and contains a list of registered devices
  const dict = entries.getOrElse([])
    .reduce((acc, current) => {
      const name = current.childrenNames && current.childrenNames[0] || current.name
      if (!acc[name]) { acc[name] = current.deviceId }
      return acc
    }, {} as Record<string, string>)

  const devices = Object.keys(dict)
    .sort((a,b) => a.localeCompare(b))
    .map((key) => ({
      name: key,
      deviceId: dict[key],
      text: key,
    } as DropListEntry))

  return [WEBSITE_ENTRY, ...devices]
}


const dropListEntriesToLinks = (
  dropListEntries: DropListEntry[],
  selected: number,
  onSelectFn: (idx: number) => void
): LinkProps[] => {
  return dropListEntries.map((entry,idx) => ({
    text: entry.text,
    isHighlighted: selected === idx,
    href: `#${idx}`, // Links with non-empty href is accessible for keyboard users.
    onClick: (e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLAnchorElement>) => {
      onSelectFn(idx)
      e.preventDefault()
    },
  }))
}

export class ConnectedFeedbackForm extends React.Component<FeedbackFormProps & ILogger, FeedbackFormState> {
  constructor(props: FeedbackFormProps & ILogger) {
    super(props)

    this.state = {
      isOpen: false,
      selected: 0,
    }
  }

  linkClickHandler() {
    if (
      Boolean(this.props.context.customerID) && // [AVWebDP-318]: mitigate 403 JS Fatal logs. No request for signed out customers.
      !this.props.devices.deviceEntries
    ) {
      getDevicesList(
        { widgetArgs: stringifyOnClient({}) as SettingsWidgetArgs}
      )
    }
    this.openModal()
  }

  selectDropListLink(selected: number) {
    this.setState({ selected })
  }

  openModal() {
    this.setState({ isOpen: true })
  }

  closeModal() {
    this.setState({ isOpen: false})
  }

  render() {
    const {
      selected,
      isOpen,
    } = this.state

    const {
      isMobile,
      isRetail,
      context,
      pageTitleId,
      logger,
      devices: {
        deviceEntries
      },
    } = this.props

    const dropListEntries = deviceEntryToDropListEntry(deviceEntries, Boolean(context.customerID))

    const dropListLinks = dropListEntriesToLinks(
      dropListEntries,
      selected,
      (s: number) => this.selectDropListLink(s)
    )

    const selectedEntry = dropListEntries[selected];
    // https://issues.amazon.com/issues/AVWebDetailPage-3533
    const formParams = {
      ASIN: pageTitleId,
      deviceName: selectedEntry.name,
      deviceTypeId: selectedEntry.deviceId,
      currentTerritory: context.currentTerritory,
      recordTerritory: context.recordTerritory,
      language: context.locale,
      marketplace: context.marketplaceID,
      encryptedCustomerId: context.customerID,
      sessionId: context.sessionID,
      sourceUrl: `${getMarketplaceUrl(context.marketplaceID)}${context.originalURI}`,
      returnUrl: buildDetailPageUrl(
        { isRetail, parameters: {}, originalURI: context.originalURI }, pageTitleId, 'atv_dp_send_feedback'
      ),
    } as FormParams

    const formUrl = `${FORM_URL_BASE}/website${isMobile ? 'Mob' : ''}/`;

    return (
      <FeedbackFormModal
        isOpen={isOpen}
        link={LINK_TEXT}
        linkOnClickFn={() => this.linkClickHandler()}
        onCloseFn={() => this.closeModal()}
        header={TITLE_TEXT}
      >
        <FeedbackFormModalContent
          dropListId={DROPLIST_ID}
          dropListLinks={dropListLinks}
          formUrl={formUrl}
          formParams={formParams}
          formOnSubmitFn={() => this.closeModal()}
          logger={logger}
        />
      </FeedbackFormModal>
    )
  }
}

const mapStateToProps = (state: IDetailPageState): FeedbackFormProps => ({
  devices: simpleSelectors.devices(state),
  isMobile: isMobileSelector(state),
  pageTitleId: simpleSelectors.pageTitleId(state),
  context: simpleSelectors.context(state),
  isRetail: isRetailSelector(state),
})

export const FeedbackForm = withLocalization(
  connect(mapStateToProps)(ConnectedFeedbackForm)
)